import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Container,
  makeStyles,
  Typography,
  lighten,
  Paper,
  Box,
  Button,
  CircularProgress,
  Divider,
  ListItemAvatar,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ButtonBase,
  Popover,
  List,
  ListItemIcon,
  Tooltip,
} from '@material-ui/core';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import EditIcon from '@material-ui/icons/Edit';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { AppState } from '../../state/MyStateProvider';
import Dashboard3Drawers from '../../page/dashboard-3-drawers/Dashboard3Drawers';
import MyAvatar from '../../widgets/avatar/MyAvatar';
import { APP_STATE_ACTION_TYPE_USER } from '../../state/seed-states';
import SeedAuthenticationRepository from '../../repository/SeedAuthenticationRepository';
import seedDrawerRoutes from '../../route/seed-drawer-routes';
import {
  drawerRoutesPropTypes,
  dashboard3DrawersIconsPropTypes,
  d3dPopoverMenusPropTypes,
  defaultD3dPopoverMenusPropTypes,
} from '../../page/dashboard-3-drawers/d3d-prop-types';
import seedLogo from '../../images/logo.svg';
import { seedDashboard3DrawersIcons } from '../../icon/seed-icons';
import MyTableRow from '../../widgets/MyTableRow';
import { MyFormattedDateTime } from '../../intl';
import FirebaseRepository from '../FirebaseRepository';
import { FirebaseState } from '../MyFirebaseProvider';
import pageUtilsPropTypes from '../../page/page-prop-types';
import TooltipIconButton from '../../widgets/TooltipIconButton';
import SeedFirebasePasswordDialog from './SeedFirebasePasswordDialog';
import SeedFirebaseUpdatePasswordDialog from './SeedFirebaseUpdatePasswordDialog';
import SeedFirebaseUpdateEmailDialog from './SeedFirebaseUpdateEmailDialog';
import SeedFirebaseUpdateProfileDialog from './SeedFirebaseUpdateProfileDialog';
import SeedFirebaseUpdatePhotoDialog from './SeedFirebaseUpdatePhotoDialog';

const useStyles = makeStyles((theme) => ({
  banner: {
    backgroundColor: () => (theme.palette.type === 'light'
      ? lighten(theme.palette.primary.main, 0.8) : lighten(theme.palette.primary.main, 0.2)),
    height: `${200 - (theme.spacing(2) * 2)}px`,
    display: 'flex',
    alignItems: 'flex-end',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  titleContainer: {
    paddingLeft: theme.spacing(2),
    overflow: 'hidden',
  },
  title: {
    display: '-webkit-box',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 1,
    overflow: 'hidden',
  },
  spacer: {
    flexGrow: 1,
  },
  textError: {
    color: theme.palette.error.main,
  },
  textSuccess: {
    color: theme.palette.success.main,
  },
  container: {
    marginBottom: theme.spacing(4),
  },
  errorButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    // '&:hover': {
    //   backgroundColor: theme.palette.error.dark,
    // },
  },
  editButton: {
    display: 'inline-block',
    marginLeft: theme.spacing(2),
  },
  photoList: {
    minWidth: 240,
  },
  changePhotoButton: {
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      '& $changePhotoButtonIcon': {
        opacity: 0.5,
      },
    },
  },
  changePhotoButtonIcon: {
    transition: theme.transitions.create(['opacity']),
    position: 'absolute',
    zIndex: 1,
    opacity: 0,
    background: theme.palette.grey[200],
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
  },
  addPhotoTooltip: {
    display: 'inline-block',
  },
  addPhotoButton: {
    height: 100,
    width: 100,
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.disabled,
    transition: theme.transitions.create(['color', 'background-color']),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.secondary,
    },
  },
}));

const defaultUserPropTypes = {
  name: '',
  username: '',
  email: '',
  avatar: '',
  createdAt: new Date(),
};

const SeedFirebaseProfilePage = (props) => {
  const {
    AuthenticationRepository,
    drawerRoutes,
    isLoading,
    applicationName,
    logo,
    pageUtils,
    icons,
    popoverMenus,
    providers,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const intl = useIntl();
  const { state, dispatch } = React.useContext(AppState);
  const userToken = state[APP_STATE_ACTION_TYPE_USER];
  const { user: userState, avatar } = userToken;

  const user = userState || defaultUserPropTypes;
  // const [user] = React.useState(userState || defaultUserPropTypes);
  // console.log("user");
  // console.log(user);

  const [openPasswordDialog, setOpenPasswordDialog] = React.useState(false);
  const [openUpdatePasswordDialog, setOpenUpdatePasswordDialog] = React.useState(false);
  const [updatePasswordType, setUpdatePasswordType] = React.useState(true);
  const [openUpdateEmailDialog, setOpenUpdateEmailDialog] = React.useState(false);
  const [updateEmailType, setUpdateEmailType] = React.useState(true);
  const [openUpdateProfileDialog, setOpenUpdateProfileDialog] = React.useState(false);
  const [photoAnchorEl, setPhotoAnchorEl] = React.useState(null);
  const [openUpdatePhotoDialog, setOpenUpdatePhotoDialog] = React.useState(false);

  let facebookProvider;
  let googleProvider;
  let githubProvider;
  let twitterProvider;
  let passwordProvider;
  user.providers.map((prov) => {
    if (prov.providerId === 'facebook.com') {
      facebookProvider = prov;
    }
    if (prov.providerId === 'google.com') {
      googleProvider = prov;
    }
    if (prov.providerId === 'github.com') {
      githubProvider = prov;
    }
    if (prov.providerId === 'twitter.com') {
      twitterProvider = prov;
    }
    if (prov.providerId === 'password') {
      passwordProvider = prov;
    }
    return null;
  });

  const allProviders = {
    'facebook.com': {
      providerId: 'facebook.com',
      providerName: 'Facebook',
      connected: facebookProvider,
      displayName: facebookProvider ? facebookProvider.displayName : '',
      email: facebookProvider ? facebookProvider.email : '',
      photoURL: facebookProvider ? facebookProvider.photoURL : '',
    },
    'google.com': {
      providerId: 'google.com',
      providerName: 'Google',
      connected: googleProvider,
      displayName: googleProvider ? googleProvider.displayName : '',
      email: googleProvider ? googleProvider.email : '',
      photoURL: googleProvider ? googleProvider.photoURL : '',
    },
    'github.com': {
      providerId: 'github.com',
      providerName: 'Github',
      connected: githubProvider,
      displayName: githubProvider ? githubProvider.displayName : '',
      email: githubProvider ? githubProvider.email : '',
      photoURL: githubProvider ? githubProvider.photoURL : '',
    },
    'twitter.com': {
      providerId: 'twitter.com',
      providerName: 'Twitter',
      connected: twitterProvider,
      displayName: twitterProvider ? twitterProvider.displayName : '',
      email: twitterProvider ? twitterProvider.email : '',
      photoURL: twitterProvider ? twitterProvider.photoURL : '',
    },
  };

  const [isLoadingVerifyEmail, setIsLoadingVerifyEmail] = React.useState(false);

  const { firebaseConfig } = React.useContext(FirebaseState);

  const [firebaseRepo] = React.useState(new FirebaseRepository(state, dispatch, firebaseConfig));

  const handleClickVerify = () => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.verifyEmail(),
      () => {
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.verifyEmail.success' }), 'success');
      },
      (error) => {
        console.log(error);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.verifyEmail.error' }), 'error');
      },
      () => {
        setIsLoadingVerifyEmail(false);
      },
    );
  };

  const handleClickLink = (providerId, providerName) => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.linkProvider(providerId),
      (payload) => {
        firebaseRepo.saveUser(payload.user);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.provider.link.success' }, { provider: providerName }), 'success');
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.provider.link.error' }, { provider: providerName }), 'error');
        }
      },
      () => {
        setIsLoadingVerifyEmail(false);
      },
    );
  };

  const handleClickUnlink = (providerId, providerName) => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.unlinkProvider(providerId),
      (payload) => {
        firebaseRepo.saveUser(payload.user);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.provider.unlink.success' }, { provider: providerName }), 'success');
      },
      (error) => {
        console.log(error);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.provider.unlink.error' }, { provider: providerName }), 'error');
      },
      () => {
        setIsLoadingVerifyEmail(false);
      },
    );
  };

  const handleClickUpdateEmail = () => {
    if (passwordProvider) {
      setUpdateEmailType(true);
    } else {
      setUpdateEmailType(false);
    }
    setOpenUpdateEmailDialog(true);
  };

  const handleConfirmUpdateEmail = (values) => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.updateEmailWithProvider(values),
      () => {
        setOpenUpdateEmailDialog(false);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.updateEmail.success' }), 'success');
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.updateEmail.error' }), 'error');
        }
      },
      () => {
        setIsLoadingVerifyEmail(false);
      },
    );
  };

  const handleConfirmUpdateProfile = (values) => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.updateProfile(values),
      () => {
        setOpenUpdateProfileDialog(false);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.updateDisplayName.success' }), 'success');
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.updateDisplayName.error' }), 'error');
        }
      },
      () => {
        setIsLoadingVerifyEmail(false);
      },
    );
  };

  const handleConfirmUpdateEmailWithPassword = (values) => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.updateEmail(values),
      () => {
        setOpenUpdateEmailDialog(false);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.updateEmail.success' }), 'success');
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.updateEmail.error' }), 'error');
        }
      },
      () => {
        setIsLoadingVerifyEmail(false);
      },
    );
  };

  const handleClickDeleteUser = () => {
    setOpenPasswordDialog(true);
  };

  const handleConfirmDeleteUser = (values) => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.deleteUser(values),
      () => {
        setIsLoadingVerifyEmail(false);
        firebaseRepo.logoutUser();
        const timestamp = new Date();
        history.push(`/login?delete=${timestamp.getTime()}`);
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.deleteUser.error' }), 'error');
        }
        setIsLoadingVerifyEmail(false);
      },
      () => {
      },
    );
  };

  const handleConfirmUpdatePassword = (values) => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.updatePassword(values),
      () => {
        setOpenUpdatePasswordDialog(false);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.updatePassword.success' }), 'success');
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.updatePassword.error' }), 'error');
        }
      },
      () => {
        setIsLoadingVerifyEmail(false);
      },
    );
  };

  const handleConfirmUpdatePasswordPopup = (values) => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.updatePasswordPopup(values),
      () => {
        setOpenUpdatePasswordDialog(false);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.setPassword.success' }), 'success');
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.setPassword.error' }), 'error');
        }
      },
      () => {
        setIsLoadingVerifyEmail(false);
      },
    );
  };

  const handleClickRemovePhoto = () => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.removeProfilePhoto(),
      () => {
        setOpenUpdatePasswordDialog(false);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.removePhoto.success' }), 'success');
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.removePhoto.error' }), 'error');
        }
      },
      () => {
        setIsLoadingVerifyEmail(false);
      },
    );
  };

  const handleConfirmUpdatePhoto = (values) => {
    setIsLoadingVerifyEmail(true);
    pageUtils.loadSomethingSilent(
      () => firebaseRepo.updateProfilePhoto(values),
      () => {
        setOpenUpdatePhotoDialog(false);
        pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.updatePhoto.success' }), 'success');
      },
      (error) => {
        if (error.message) {
          pageUtils.showSnackbar(error.message, 'error');
        } else {
          pageUtils.showSnackbar(intl.formatMessage({ id: 'seed.firebase.authentication.updatePhoto.error' }), 'error');
        }
      },
      () => {
        setIsLoadingVerifyEmail(false);
      },
    );
  };

  const columns = [
    {
      type: 'heading',
      labelId: 'seed.firebase.authentication.user.profile',
    },
    {
      type: 'twoColumns',
      labelId: 'seed.firebase.authentication.user.photoURL',
      content: (
        <>
          {
            user.photoURL ? (
              <Tooltip title={<FormattedMessage id="seed.firebase.authentication.updatePhoto.button.updateRemove" />}>
                <ButtonBase
                  className={classes.changePhotoButton}
                  onClick={(e) => setPhotoAnchorEl(e.currentTarget)}
                >
                  <div className={classes.changePhotoButtonIcon}>
                    <AddAPhotoIcon fontSize="large" />
                  </div>
                  <MyAvatar
                    size={100}
                    src={user.photoURL}
                    name={user.displayName || user.email}
                    alt={user.displayName || user.email}
                  />
                </ButtonBase>
              </Tooltip>
            ) : (
              <TooltipIconButton
                tooltipProps={{
                  className: classes.addPhotoTooltip,
                }}
                className={classes.addPhotoButton}
                onClick={(e) => setPhotoAnchorEl(e.currentTarget)}
                title={<FormattedMessage id="seed.firebase.authentication.addPhoto.button" />}
                icon={<AddAPhotoIcon fontSize="large" />}
              />
            )
          }
          <Popover
            open={Boolean(photoAnchorEl)}
            anchorEl={photoAnchorEl}
            onClose={() => setPhotoAnchorEl(null)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <List className={classes.photoList}>
              <ListItem button onClick={() => setOpenUpdatePhotoDialog(true)}>
                <ListItemIcon>
                  <AddAPhotoIcon />
                </ListItemIcon>
                <ListItemText
                  primary={intl.formatMessage({ id: !user.photoURL ? 'seed.firebase.authentication.addPhoto.button' : 'seed.firebase.authentication.updatePhoto.button' })}
                />
              </ListItem>
              <ListItem disabled={!user.photoURL} button onClick={handleClickRemovePhoto}>
                <ListItemIcon>
                  <DeleteForeverIcon />
                </ListItemIcon>
                <ListItemText
                  primary={intl.formatMessage({ id: 'seed.firebase.authentication.removePhoto.button' })}
                />
              </ListItem>
            </List>
          </Popover>
        </>
      ),
    },
    {
      type: 'twoColumns',
      labelId: 'seed.firebase.authentication.user.displayName',
      content: (
        <>
          <Typography variant="body2" display="inline">
            {user.displayName || '-'}
          </Typography>
          <TooltipIconButton
            tooltipProps={{
              className: classes.editButton,
            }}
            size="small"
            title={<FormattedMessage id={!user.displayName ? 'seed.firebase.authentication.updateDisplayName.button.add' : 'seed.firebase.authentication.updateDisplayName.button.update'} />}
            icon={<EditIcon fontSize="small" />}
            onClick={() => setOpenUpdateProfileDialog(true)}
          />
        </>
      ),
    },
    {
      type: 'twoColumns',
      labelId: 'seed.firebase.authentication.user.phoneNumber',
      content: (
        <Typography variant="body2">
          {user.phoneNumber || '-'}
        </Typography>
      ),
    },
    {
      type: 'heading',
      labelId: 'seed.firebase.authentication.user.authentication',
    },
    {
      type: 'twoColumns',
      labelId: 'seed.firebase.authentication.user.email',
      content: (
        <>
          <Typography variant="body2" display="inline">
            {user.email || '-'}
          </Typography>
          <TooltipIconButton
            tooltipProps={{
              className: classes.editButton,
            }}
            size="small"
            title={<FormattedMessage id="seed.firebase.authentication.updateEmail.button.update" />}
            icon={<EditIcon fontSize="small" />}
            onClick={handleClickUpdateEmail}
          />
        </>
      ),
    },
    {
      type: 'twoColumns',
      labelId: 'seed.firebase.authentication.user.emailVerified',
      content: (
        <>
          <Typography
            variant="body2"
            className={clsx([], {
              [classes.textSuccess]: user.emailVerified,
              [classes.textError]: !user.emailVerified,
            })}
          >
            <FormattedMessage
              id={user.emailVerified ? 'seed.firebase.authentication.user.emailVerified.true' : 'seed.firebase.authentication.user.emailVerified.false'}
            />
          </Typography>
          {
            user.emailVerified ? null : (
              <Box mt={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleClickVerify}
                  disabled={isLoadingVerifyEmail}
                  startIcon={isLoadingVerifyEmail ? <CircularProgress size={20} /> : ''}
                >
                  <FormattedMessage id="seed.firebase.authentication.verifyEmail.button" />
                </Button>
              </Box>
            )
          }
        </>
      ),
    },
    {
      type: 'twoColumns',
      labelId: 'seed.firebase.authentication.user.provider.password',
      content: (
        <>
          {passwordProvider ? (
            <>
              <Button
                onClick={() => {
                  setOpenUpdatePasswordDialog(true);
                  setUpdatePasswordType(true);
                }}
                variant="outlined"
                size="small"
              >
                <FormattedMessage id="seed.firebase.authentication.updatePassword.button" />
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setOpenUpdatePasswordDialog(true);
                setUpdatePasswordType(false);
              }}
              variant="outlined"
              size="small"
            >
              <FormattedMessage id="seed.firebase.authentication.setPassword.button" />
            </Button>
          )}
        </>
      ),
    },
    {
      type: 'heading',
      labelId: 'seed.firebase.authentication.user.provider',
    },
  ];

  providers.map((provider) => {
    const prov = allProviders[provider];
    columns.push({
      type: 'twoColumns',
      labelId: prov.providerId,
      content: (
        <>
          {
            prov.connected ? (
              <ListItem ContainerComponent="div">
                <ListItemAvatar>
                  <MyAvatar
                    src={prov.photoURL}
                    name={prov.displayName || prov.email}
                    alt={prov.displayName || prov.email}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={prov.displayName}
                  secondary={(
                    <>
                      <Typography variant="caption" display="block">
                        {prov.email}
                      </Typography>
                      <Typography variant="caption" className={classes.textSuccess}>
                        <FormattedMessage id="seed.firebase.authentication.provider.linked" />
                      </Typography>
                    </>
                  )}
                />
                <ListItemSecondaryAction>
                  <TooltipIconButton
                    title={<FormattedMessage id="seed.firebase.authentication.provider.unlink.button" values={{ provider: prov.providerName }} />}
                    icon={isLoadingVerifyEmail ? <CircularProgress size={20} /> : <LinkOffIcon />}
                    disabled={isLoadingVerifyEmail}
                    onClick={() => handleClickUnlink(prov.providerId, prov.providerName)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ) : (
              <Box mb={1}>
                <Typography
                  variant="body2"
                  className={clsx([], {
                    [classes.textSuccess]: prov.connected,
                  })}
                  gutterBottom
                >
                  <FormattedMessage id="seed.firebase.authentication.provider.unlinked" />
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => handleClickLink(
                    prov.providerId, prov.providerName,
                  )}
                  disabled={isLoadingVerifyEmail}
                  startIcon={isLoadingVerifyEmail ? <CircularProgress size={20} /> : ''}
                >
                  <FormattedMessage id="seed.firebase.authentication.provider.link.button" values={{ provider: prov.providerName }} />
                </Button>
              </Box>
            )
          }
          <Divider />
        </>
      ),
    });
    return null;
  });

  const othersColumns = [
    {
      type: 'heading',
      labelId: 'seed.firebase.authentication.user.others',
    },
    {
      type: 'twoColumns',
      labelId: 'seed.firebase.authentication.user.creationTime',
      content: (
        <Typography variant="body2">
          {user.creationTime ? <MyFormattedDateTime value={new Date(user.creationTime)} /> : '-'}
        </Typography>
      ),
    },
    {
      type: 'twoColumns',
      labelId: 'seed.firebase.authentication.user.lastSignInTime',
      content: (
        <Typography variant="body2">
          {user.lastSignInTime ? <MyFormattedDateTime value={new Date(user.lastSignInTime)} /> : '-'}
        </Typography>
      ),
    },
    {
      type: 'heading',
      labelId: 'seed.firebase.authentication.user.dangerous',
    },
    {
      type: 'twoColumns',
      content: (
        <Button
          variant="outlined"
          onClick={handleClickDeleteUser}
          className={classes.errorButton}
        >
          <FormattedMessage id="seed.firebase.authentication.deleteUser.button" />
        </Button>
      ),
    },
  ];

  othersColumns.map((col) => {
    columns.push(col);
    return null;
  });

  return (
    <Dashboard3Drawers
      AuthenticationRepository={AuthenticationRepository}
      titleId="seed.profile.title"
      applicationName={applicationName}
      logo={logo}
      icons={icons}
      routes={drawerRoutes}
      isLoading={isLoading}
      useBackButton
      usePaddingContent={false}
      popoverMenus={popoverMenus}
    >
      <Container className={classes.banner}>
        <MyAvatar
          size={60}
          src={avatar ? avatar.avatar : ''}
          alt={avatar ? avatar.title : ''}
          name={avatar ? avatar.title : ''}
        />
        <div className={classes.titleContainer}>
          <Typography className={classes.title} variant="h5" color="textPrimary">
            {avatar ? avatar.title : ''}
          </Typography>
          <Typography className={classes.title} variant="body2" color="textSecondary">
            {avatar ? avatar.subtitle : ''}
          </Typography>
        </div>
        <div className={classes.spacer} />
      </Container>
      <Container className={classes.container} maxWidth="md">
        <Paper>
          <Box p={2}>
            <MyTableRow
              columns={columns}
            />
          </Box>
        </Paper>
      </Container>
      <SeedFirebasePasswordDialog
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
        isLoading={isLoadingVerifyEmail}
        onSubmit={handleConfirmDeleteUser}
        icons={icons}
      />
      <SeedFirebaseUpdatePasswordDialog
        open={openUpdatePasswordDialog}
        onClose={() => setOpenUpdatePasswordDialog(false)}
        isLoading={isLoadingVerifyEmail}
        onSubmit={handleConfirmUpdatePasswordPopup}
        onSubmitWithPassword={handleConfirmUpdatePassword}
        icons={icons}
        withPassword={updatePasswordType}
      />
      <SeedFirebaseUpdateEmailDialog
        open={openUpdateEmailDialog}
        onClose={() => setOpenUpdateEmailDialog(false)}
        isLoading={isLoadingVerifyEmail}
        onSubmit={handleConfirmUpdateEmail}
        onSubmitWithPassword={handleConfirmUpdateEmailWithPassword}
        icons={icons}
        email={user.email}
        withPassword={updateEmailType}
      />
      <SeedFirebaseUpdateProfileDialog
        open={openUpdateProfileDialog}
        onClose={() => setOpenUpdateProfileDialog(false)}
        isLoading={isLoadingVerifyEmail}
        onSubmit={handleConfirmUpdateProfile}
        icons={icons}
        displayName={user.displayName}
      />
      <SeedFirebaseUpdatePhotoDialog
        open={openUpdatePhotoDialog}
        onClose={() => setOpenUpdatePhotoDialog(false)}
        isLoading={isLoadingVerifyEmail}
        onSubmit={handleConfirmUpdatePhoto}
        icons={icons}
        photoURL={user.photoURL}
      />
    </Dashboard3Drawers>
  );
};

SeedFirebaseProfilePage.propTypes = {
  isLoading: PropTypes.bool,
  AuthenticationRepository: PropTypes.func,
  drawerRoutes: drawerRoutesPropTypes,
  applicationName: PropTypes.string,
  logo: PropTypes.string,
  icons: dashboard3DrawersIconsPropTypes,
  pageUtils: pageUtilsPropTypes.isRequired,
  popoverMenus: d3dPopoverMenusPropTypes,
  providers: PropTypes.arrayOf(PropTypes.string),
};

SeedFirebaseProfilePage.defaultProps = {
  isLoading: false,
  AuthenticationRepository: SeedAuthenticationRepository,
  drawerRoutes: seedDrawerRoutes,
  applicationName: 'react-seed',
  logo: seedLogo,
  icons: seedDashboard3DrawersIcons,
  popoverMenus: defaultD3dPopoverMenusPropTypes,
  providers: [
    'google.com',
    'facebook.com',
    'twitter.com',
    'github.com',
  ],
};

export default SeedFirebaseProfilePage;
